// src/shared/components/FormSaveBar.tsx

import { Box, Button, Group, Text, Transition } from '@mantine/core';

interface FormSaveBarProps {
  isDirty: boolean;
  isPending: boolean;
  /** Omit to hide the Discard button (e.g. create flows with a single action). */
  onDiscard?: () => void;
  /** Extra gate ORed into the save button's disabled state (e.g. a refId conflict). */
  saveDisabled?: boolean;
  saveLabel?: string;
  /** Associates the save button with a <form id> when the bar renders outside that form. */
  formId?: string;
  /**
   * Keep the bar mounted while the form is clean. Needed for create flows,
   * where an untouched form must remain submittable.
   */
  forceVisible?: boolean;
}

/**
 * Contextual save bar for edit forms (Shopify / YouTube Studio pattern):
 * hidden while the form is clean, slides in flush under the AppShell header
 * when there are unsaved changes. Fixed to the frame — spans the content area
 * (AppShell offset vars resolve to 0 when the navbar is collapsed) and stays
 * put regardless of page scroll, so it never gaps against the header the way
 * the old in-flow sticky bars did.
 *
 * Must be rendered INSIDE the <form> it submits (native type="submit"), or
 * given `formId` when it can't be. Do not wrap in a Portal — that detaches
 * the submit button from the form.
 */
export function FormSaveBar({
  isDirty,
  isPending,
  onDiscard,
  saveDisabled = false,
  saveLabel = 'Save Changes',
  formId,
  forceVisible = false,
}: FormSaveBarProps) {
  const visible = forceVisible || isDirty || isPending;

  return (
    <Transition mounted={visible} transition="slide-down">
      {(transitionStyles) => (
        <Box
          pos="fixed"
          top="var(--app-shell-header-offset, 60px)"
          left="var(--app-shell-navbar-offset, 0px)"
          right={0}
          bg="var(--mantine-color-body)"
          px="md"
          py="sm"
          style={{
            ...transitionStyles,
            // Above page content; below AppShell sections (mobile navbar
            // overlay) and Mantine overlays (dropdowns/modals at 200+).
            zIndex: 90,
            borderBottom: '1px solid var(--mantine-color-gray-3)',
            boxShadow: 'var(--mantine-shadow-sm)',
          }}
        >
          <Group justify="space-between">
            {isDirty ? (
              <Text size="sm" c="dimmed" fw={500}>
                Unsaved changes
              </Text>
            ) : (
              <div />
            )}
            <Group gap="sm">
              {onDiscard && (
                <Button
                  variant="default"
                  onClick={onDiscard}
                  disabled={!isDirty || isPending}
                >
                  Discard
                </Button>
              )}
              <Button
                type="submit"
                form={formId}
                loading={isPending}
                disabled={saveDisabled || (!isDirty && !forceVisible)}
              >
                {saveLabel}
              </Button>
            </Group>
          </Group>
        </Box>
      )}
    </Transition>
  );
}
